/**
 * Messages service for the ms-agents backend (Flex Comms module).
 *
 * Each function takes the ms-agents verb (imported from `commsApi.js`) as its
 * first argument. Return-shape note: `get` returns `response.data`; `post`/`remove`
 * return `{ data, status }`.
 *
 * MessageOut = { id, room_id, sender_id, message_type, content_type, content,
 *   sender_role, reply_to_id, coach_only, ai_generated, retracted, seq, status,
 *   created_at, mentions?, attachment? }. message_type: 0=client, 1=coach/bot/staff,
 *   2=system.
 */
import { buildQuery } from "./_query";
import { wrapCommsError } from "./_error";

/**
 * Fetch a page of messages for a room (reverse chronological, paged by seq).
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {string} roomId - Room id.
 * @param {Object} [options] - `{ limit=50, before_seq }`.
 * @returns {Promise<{ messages: Array<Object>, has_more: boolean, next_before_seq: number }>}
 */
export const getMessages = async (get, roomId, { limit = 50, before_seq } = {}) => {
  try {
    return await get(`/api/v1/rooms/${roomId}/messages${buildQuery({ limit, before_seq })}`);
  } catch (err) {
    // Throws with `status` attached — a 403 here ("not a participant") is a
    // permanent answer for this room, and the thread stops polling on it.
    throw wrapCommsError(err, "Error fetching messages");
  }
};

/**
 * Send a message to a room.
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {string} roomId - Room id.
 * @param {Object} payload - `{ content, content_type, content_attributes?,
 *   reply_to_id?, coach_only?, idempotency_key?, mentions?, attachment? }`.
 * @returns {Promise<{ data: { message: Object, created: boolean }, status: number }>}
 */
export const sendMessage = async (post, roomId, payload) => {
  try {
    return await post(`/api/v1/rooms/${roomId}/messages`, payload);
  } catch (err) {
    throw wrapCommsError(err, "Error sending message");
  }
};

/**
 * Send ONE message into many rooms — the dock's multi-select send.
 *
 * Each recipient gets a normal message in their own 1:1 room. This is not a
 * group chat, and it is not a broadcast (`broadcastsService`), which is
 * push/email addressed by segment and never reaches a chat room.
 *
 * Fire and forget: the call returns as soon as the server has taken the batch.
 *
 * **202, and the response carries no outcomes.** Nothing has been sent when
 * this resolves — the server accepts the batch and delivers it in the
 * background. Progress arrives as `bulk_send_started` / `bulk_send_progress` /
 * `bulk_send_done` events on `broadcast:{user_id}`, the personal channel
 * `useUserChannel` already holds, correlated by the returned `batch_id`.
 * `features/chatDock/bulkSendStore` consumes them.
 *
 * A room the caller cannot write to therefore surfaces as a failed progress
 * event, not an error here: the request is accepted before any room is looked
 * at. A rejection from this call means the request itself was malformed —
 * empty `room_ids`, more than 200, or a `content_type`/`attachments` mismatch.
 *
 * **Events are never replayed.** The `broadcast` namespace has no Centrifugo
 * history, so a client that is disconnected when one fires does not get it
 * later. Treat a stalled batch as unknown rather than failed — the messages
 * almost certainly landed; only the notifications were lost.
 *
 * **Retry with the same `idempotency_key`.** The server's uniqueness is
 * `(room_id, idempotency_key)`, so one key is exactly-once per room and makes
 * re-sending the whole request safe — rooms that already landed replay as
 * `created: false` and nobody is messaged twice.
 *
 * `coach_only`, `reply_to_id` and `mentions` are rejected by this endpoint:
 * the first would fan an internal note out to every selected client, the rest
 * are meaningless across a set of rooms.
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {Object} payload - `{ room_ids (1..200), content, content_type,
 *   content_attributes?, attachments?, idempotency_key? }`.
 * @returns {Promise<{ data: { batch_id: string, total: number,
 *   room_ids: string[] }, status: number }>}
 */
export const bulkSendMessage = async (post, payload) => {
  try {
    return await post(`/api/v1/rooms/messages/bulk`, payload);
  } catch (err) {
    throw wrapCommsError(err, "Error sending messages");
  }
};

/**
 * Delete (retract) a message.
 *
 * @param {Function} remove - ms-agents DELETE verb.
 * @param {string} roomId - Room id.
 * @param {string} messageId - Message id.
 * @returns {Promise<{ data: Object, status: number }>}
 */
export const deleteMessage = async (remove, roomId, messageId) => {
  try {
    return await remove(`/api/v1/rooms/${roomId}/messages/${messageId}`);
  } catch (err) {
    throw wrapCommsError(err, "Error deleting message");
  }
};

/**
 * Mark messages read up to a sequence number.
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {string} roomId - Room id.
 * @param {number} upToSeq - Highest read seq.
 * @returns {Promise<{ data: Object, status: number }>}
 */
export const markRead = async (post, roomId, upToSeq) => {
  try {
    return await post(`/api/v1/rooms/${roomId}/read`, { up_to_seq: upToSeq });
  } catch (err) {
    throw wrapCommsError(err, "Error marking messages read");
  }
};

/**
 * Mark messages delivered up to a sequence number.
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {string} roomId - Room id.
 * @param {number} upToSeq - Highest delivered seq.
 * @returns {Promise<{ data: Object, status: number }>}
 */
export const markDelivered = async (post, roomId, upToSeq) => {
  try {
    return await post(`/api/v1/rooms/${roomId}/delivered`, { up_to_seq: upToSeq });
  } catch (err) {
    throw wrapCommsError(err, "Error marking messages delivered");
  }
};

/**
 * Get read/delivery receipts for a room.
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {string} roomId - Room id.
 * @returns {Promise<{ reads: Array<{ user_id, participant_role, last_read_seq,
 *   last_delivered_seq, last_read_at }> }>}
 */
export const getReads = async (get, roomId) => {
  try {
    return await get(`/api/v1/rooms/${roomId}/reads`);
  } catch (err) {
    throw wrapCommsError(err, "Error fetching read receipts");
  }
};

/**
 * Publish a typing indicator for a room.
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {string} roomId - Room id.
 * @param {boolean} started - True when typing starts, false when it stops.
 * @returns {Promise<{ data: Object, status: number }>}
 */
export const setTyping = async (post, roomId, started) => {
  try {
    return await post(`/api/v1/rooms/${roomId}/typing`, { started });
  } catch (err) {
    throw wrapCommsError(err, "Error setting typing indicator");
  }
};

/**
 * Submit thumbs-up/down feedback on an AI-generated message.
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {string} roomId - Room id.
 * @param {string} messageId - Message id.
 * @param {"THUMBS_UP"|"THUMBS_DOWN"} feedback - Feedback value.
 * @returns {Promise<{ data: Object, status: number }>}
 */
export const aiFeedback = async (post, roomId, messageId, feedback) => {
  try {
    return await post(`/api/v1/rooms/${roomId}/messages/${messageId}/ai-feedback`, { feedback });
  } catch (err) {
    throw wrapCommsError(err, "Error submitting AI feedback");
  }
};

/**
 * Flag a message for moderation.
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {string} roomId - Room id.
 * @param {string} messageId - Message id.
 * @param {string} reason - Flag reason.
 * @returns {Promise<{ data: Object, status: number }>}
 */
export const flagMessage = async (post, roomId, messageId, reason) => {
  try {
    return await post(`/api/v1/rooms/${roomId}/messages/${messageId}/flag`, { reason });
  } catch (err) {
    throw wrapCommsError(err, "Error flagging message");
  }
};

/**
 * Remove a flag from a message.
 *
 * @param {Function} remove - ms-agents DELETE verb.
 * @param {string} roomId - Room id.
 * @param {string} messageId - Message id.
 * @returns {Promise<{ data: Object, status: number }>}
 */
export const unflagMessage = async (remove, roomId, messageId) => {
  try {
    return await remove(`/api/v1/rooms/${roomId}/messages/${messageId}/flag`);
  } catch (err) {
    throw wrapCommsError(err, "Error unflagging message");
  }
};

/**
 * Request a presigned upload URL for an attachment.
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {Object} payload - `{ file_name, content_type, file_size, scope }`.
 *   scope: `chat` | `ai` | `support`.
 * @returns {Promise<{ data: { upload_url, data_url, blob_path,
 *   content_type_bucket, expires_at }, status: number }>}
 */
export const presignUpload = async (post, payload) => {
  try {
    return await post(`/api/v1/uploads/presign`, payload);
  } catch (err) {
    throw wrapCommsError(err, "Error creating presigned upload");
  }
};

/**
 * Exchange an attachment's `blob_path` for a short-lived signed URL.
 *
 * A media `data_url` is the blob's identity, not a fetchable link — the
 * container is private. This endpoint re-checks room authorization and returns
 * a read-only SAS valid for ~15 minutes.
 *
 * Web must use this rather than `/uploads/download`: that route answers 302,
 * and once a CORS request is redirected across origins the browser replaces
 * `Origin` with `null`, which no storage CORS rule can match. The URL returned
 * here is assigned straight to a `src`, and media element loads aren't
 * CORS-gated at all.
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {string} blobPath - `media.blob_path` from the attachment.
 * @returns {Promise<{ url: string, expires_at: string, blob_path: string }>}
 */
export const getMediaUrl = async (get, blobPath) => {
  try {
    return await get(
      `/api/v1/uploads/media-url?blob_path=${encodeURIComponent(blobPath)}`
    );
  } catch (err) {
    const status = err?.response?.status;
    if (status === 403) throw new Error("You don't have access to this attachment.");
    if (status === 404) throw new Error("This attachment is no longer available.");
    throw new Error("Attachment could not be loaded.");
  }
};
